import SEOHead from "@/components/SEOHead";
import Layout from "@/components/Layout";
import HeroSection from "@/components/HeroSection";
import UpdateNoticeSection from "@/components/UpdateNoticeSection";
import PartnerStatusSection from "@/components/PartnerStatusSection";
import TrustSection from "@/components/TrustSection";
import ProductWorldsSection from "@/components/ProductWorldsSection";
import ProductSpotlightSection from "@/components/ProductSpotlightSection";
import AddRedSection from "@/components/AddRedSection";
import SelectionGuideSection from "@/components/SelectionGuideSection";
import ExperienceSection from "@/components/ExperienceSection";
import HowToSection from "@/components/HowToSection";
import PartnerBenefitSection from "@/components/PartnerBenefitSection";
import PartnerlinkSupportSection from "@/components/PartnerlinkSupportSection";
import FAQSection from "@/components/FAQSection";
import FinalCTASection from "@/components/FinalCTASection";
import ExpertSection from "@/components/ExpertSection";
import DisclosureSection from "@/components/DisclosureSection";

const PAGE_TITLE = "heilys Partnerlink & Produktinfos – unabhängige Übersicht";
const PAGE_DESCRIPTION =
  "heilys.info ordnet die Marke heilys unabhängig ein: Produktinfos zu Gen30+ Vision, AddRed, Panels und Wearables sowie der direkte Weg in den offiziellen heilys Shop.";

const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Wo kann ich heilys Produkte kaufen?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "heilys Produkte sind ausschließlich über den offiziellen heilys Shop erhältlich. heilys.info ist eine unabhängige Informationsseite und verkauft selbst nichts, sondern verlinkt für den Kauf direkt zum Hersteller.",
      },
    },
    {
      "@type": "Question",
      name: "Was hat sich am heilys Partnerprogramm geändert?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "heilys hat das Partnerprogramm zum 31.07.2026 umgestellt. Konditionen werden seitdem ausschließlich zentral über den offiziellen Shop geregelt und gelten dort für alle Kunden gleichermaßen.",
      },
    },
    {
      "@type": "Question",
      name: "Warum sollte ich über den Partnerlink kaufen?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Der Kauf über den Partnerlink verursacht keine Mehrkosten und unterstützt unabhängige Tests, Videos und Erfahrungsberichte von Longevity Hackers.",
      },
    },
    {
      "@type": "Question",
      name: "Welche heilys Produkte sind besonders interessant?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Besonders spannend sind die Gen30+Vision und die AddRed Schreibtischlampe, weil sie unterschiedliche Anwendungsbereiche abdecken: Rotlichttherapie, Nahinfrarot, Bildschirmarbeit, Abendroutine und Longevity.",
      },
    },
    {
      "@type": "Question",
      name: "Ist heilys eine gute Alternative zu Mito Light?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "heilys und Mito Light verfolgen unterschiedliche Produktansätze. heilys ist besonders interessant für Nutzer, die Wert auf Design, Alltagstauglichkeit und bestimmte Spezialanwendungen legen.",
      },
    },
    {
      "@type": "Question",
      name: "Ist heilys.info der offizielle heilys Shop?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Nein. heilys.info ist eine unabhängige Informationsseite und hat keine direkte Verbindung zum Unternehmen heilys. Bei Käufen über Partnerlinks kann eine Provision anfallen – für dich bleibt der Preis unverändert.",
      },
    },
    {
      "@type": "Question",
      name: "Wie sind die heilys Gen30 Erfahrungen?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Das heilys Gen30+Vision ist ein kompaktes Rotlichtgerät mit 36 Dual-Chip-LEDs, 30 W und sechs Wellenlängen (630, 660, 670, 810, 830, 850 nm). Nutzer schätzen vor allem den Vision-Modus mit 670 nm und die kompakte Bauform.",
      },
    },
  ],
};

const articleStructuredData = {
  "@context": "https://schema.org",
  "@type": "Article",
  "@id": "https://heilys.info/#article",
  headline: "heilys Partnerlink & Produktinfos – unabhängige Übersicht",
  description:
    "Unabhängige Übersicht zur Marke heilys: Produktkategorien, Wellenlängen, Anwendung und der Weg in den offiziellen heilys Shop über den Partnerlink.",
  image: [
    "https://heilys.info/images/hero-main.webp",
    "https://heilys.info/images/heilys_4.webp",
    "https://heilys.info/images/heilys_3.webp",
  ],
  datePublished: "2026-01-15",
  dateModified: "2026-08-01",
  inLanguage: "de",
  url: "https://heilys.info/",
  mainEntityOfPage: "https://heilys.info/",
  author: {
    "@type": "Person",
    name: "Benedikt Keck",
    jobTitle: "Longevity-Experte",
    url: "https://www.longevity-hackers.de/about",
    worksFor: {
      "@type": "Organization",
      name: "Longevity Hackers",
      url: "https://www.longevity-hackers.de/",
    },
  },
  publisher: {
    "@type": "Organization",
    name: "heilys.info",
    url: "https://heilys.info/",
    logo: {
      "@type": "ImageObject",
      url: "https://heilys.info/favicon.png",
      width: 180,
      height: 180,
    },
  },
  about: { "@type": "Brand", name: "heilys" },
  mentions: [
    { "@type": "Thing", name: "heilys Gen30+ Vision" },
    { "@type": "Thing", name: "heilys AddRed" },
    { "@type": "Thing", name: "Rotlichttherapie" },
    { "@type": "Thing", name: "Photobiomodulation" },
  ],
};

const breadcrumbStructuredData = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Startseite",
      item: "https://heilys.info/",
    },
  ],
};

const Index = () => (
  <Layout>
    <SEOHead
      title={PAGE_TITLE}
      description={PAGE_DESCRIPTION}
      canonical="https://heilys.info/"
      ogImage="https://heilys.info/images/hero-main.webp"
      ogImageAlt="heilys Partnerlink und Produktinfos – unabhängige Übersicht"
      schemaJson={[articleStructuredData, breadcrumbStructuredData, faqStructuredData]}
    />
    <HeroSection />
    <UpdateNoticeSection />
    <PartnerStatusSection />
    <TrustSection />
    <ProductWorldsSection />
    <ProductSpotlightSection />
    <AddRedSection />
    <SelectionGuideSection />
    <ExperienceSection />
    <HowToSection />
    <PartnerBenefitSection />
    <PartnerlinkSupportSection />
    <FAQSection />
    <FinalCTASection />
    <ExpertSection />
    <DisclosureSection />
  </Layout>
);

export default Index;
